import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Trash, Check } from "lucide-react";
import carImage from "../../Assets/Rectangle86.png";
import { getCarDetail, deleteCar } from "../../api/index";
import { Loader } from "../../Components/Loader";
import { ToastMsg } from "../../Components/ToastMsg";

const CarDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const res = await getCarDetail(id);
        console.log("Car Detail API Response:", res.data.data);
        setCar(res.data.data);
      } catch (error) {
        console.error("Error fetching car detail:", error);
      }
    };

    if (id) fetchCar();
  }, [id]);

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const res = await deleteCar(id);
      setIsDeleteModalOpen(false);
      ToastMsg(res?.data?.message, "success");
      navigate("/car-listings"); // Redirect to listings page
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      ToastMsg(error?.response?.data?.message, "error");
    } finally {
      closeDeleteModal()
      setIsDeleting(false);
    }
  };

  if (!car) {
    return <Loader />;
  }

  const featuresArray = car.features
    ? car.features.split(",").map((f) => f.trim())
    : [];
    console.log('data',car)

  return (
    <>
      <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen text-white">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#F3B753] mb-4 hover:underline"
        >
          <ArrowLeft size={18} />
          Back
        </button>

        {/* Heading */}
        <h2 className="text-2xl font-semibold mb-1 pb-4 border-b border-[#333]">
          Vehicle Details
        </h2>

        {/* Image + Info Section */}
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex flex-1 gap-4">
            {/* Main Image */}
            <div className="w-[900px] rounded-[7.62px] overflow-hidden bg-white">
             {car?.thumnail_image ? <img
                src={car?.thumnail_image}
                alt={`${car.brand} ${car.model}`}
                className="w-[900px] h-[400px] object-cover rounded-[7.62px]"
              /> : <div className="w-[900px] h-[400px] bg-gray-500 flex justify-center items-center border-none ">Not Available</div>}
            </div>

            {/* Thumbnails */}
            <div className="flex flex-col gap-[17.89px] w-[205px]">
              {
                
                car?.other_images?.slice(0,3)?.map((item, i) => (
                  <div key={i} className="rounded overflow-hidden bg-white">
                   {item? <img
                      src={item}
                      alt={`thumb-${i}`}
                      className="w-[205px] h-[119px] object-cover rounded-[7.29px]"
                    /> : <div className="w-[205px]  h-[119px] bg-gray-500 flex justify-center items-center border-none ">Not Available</div>}
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Info & Actions */}
        <div className="w-full flex justify-between">
          <div className="flex flex-col gap-3 mt-4 lg:mt-0">
            <p className="text-sm text-[#999]">
              Hosted by:{" "}
              <span className="text-white font-medium">Mr Rent</span>
            </p>
            <h3 className="text-2xl font-semibold">
              {car.brand} {car.model} ({car.year})
            </h3>
            <p className="text-sm text-[#ccc]">
              {car.transmission} • {car.fuel_type} • {car.seats} seats •{" "}
              {car.price_per_km} KMs
            </p>
          </div>

          <div className="flex flex-col gap-2 mt-6 lg:mt-0">
            <div className="flex justify-between gap-2">
              <button
                onClick={() =>
                  navigate('/car-listings/edit', {
                    state: { mode: 'edit', data: car },
                  })
                }
                className="flex items-center justify-center gap-2 w-[120px] h-[40px] 
                rounded-[3px] bg-gradient-to-b from-white/20 to-[#999999]/0 
                text-[#F6F6F6] font-poppins font-medium text-sm
                hover:from-white/30 hover:to-[#999999]/10 transition"
              >
                <Edit size={20} strokeWidth={1.5} /> Edit Info
              </button>

              <button
                onClick={openDeleteModal}
                className="flex items-center justify-center gap-2 w-[120px] h-[40px] 
                rounded-[3px] bg-gradient-to-b from-white/20 to-[#999999]/0 
                text-[#F6F6F6] font-poppins font-medium text-sm
                hover:from-white/30 hover:to-[#999999]/10 transition"
              >
                <Trash size={20} strokeWidth={1.5} /> Remove
              </button>
            </div>
            <div className="text-[32px] font-bold text-white">
              € {car.price_per_day}
              <span className="text-sm font-medium text-[#aaa]">/day</span>
            </div>
          </div>
        </div>

        {/* Features Section */}
        {featuresArray.length > 0 && (
          <div
            className="my-8 border border-[#F6e19b] rounded-lg p-4 flex flex-col 
                      bg-gradient-to-b from-[rgba(255,242,221,0.05)] to-[rgba(235,233,230,0.05)] 
                      backdrop-blur-[18.6px]"
          >
            <div className="font-semibold text-base mb-4 font-['Poppins']">
              FEATURES
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {featuresArray.map((feature, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="bg-gray-500 rounded-full p-0.5">
                      <Check size={15} />
                    </div>
                    <span className="font-poppins font-normal text-[#F6F6F6] text-[14px] leading-[21px]">
                      {feature}
                    </span>
                  </div>
                  {idx !== featuresArray.length - 1 && (
                    <span
                      className="h-5 border-l border-[#F6F6F6] opacity-20 mx-2"
                      style={{ width: "0.75px" }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Vehicle Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-[#1E1E1E] rounded-xl shadow-lg w-[400px] p-6 text-center border border-[#F9AC1D] bg-gradient-to-b from-[#2b220fdb] to-[#18140bec]">
            <h2 className="text-xl font-semibold mb-2">Delete Vehicle?</h2>
            <p className="text-sm text-gray-300 mb-6">
              Are you sure you want to delete this vehicle?
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={closeDeleteModal}
                disabled={isDeleting}
                className="flex-1 border border-[#F3B753] text-[#F3B753] py-3 rounded-lg font-medium text-sm hover:bg-[#F3B753] hover:text-black transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 bg-[#F3B753] text-black py-2 rounded-lg font-medium text-sm hover:bg-[#e6a800] transition disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CarDetails;
